// 滞在とレシート・カード利用の突き合わせ
import { format } from 'date-fns'

import type { CardTransaction } from '@/api/types/card-transaction'
import type { OverlandVisit } from '@/api/types/overland'
import type { ReceiptSummary } from '@/api/types/receipt'
import { parseUtcDate } from '@/lib/date-utils'

/** 利用時刻を HH:mm で返す。売上到着分のように時刻が無いものは「時刻不明」 */
export const formatSpendingTime = (iso: string | null): string =>
  iso ? format(parseUtcDate(iso), 'HH:mm') : '時刻不明'

const isWithinVisit = (visit: OverlandVisit, time: number): boolean => {
  if (!visit.arrival_date) return false
  const start = parseUtcDate(visit.arrival_date).getTime()
  const end = visit.departure_date ? parseUtcDate(visit.departure_date).getTime() : Date.now()
  return time >= start && time <= end
}

/**
 * 滞在ごとのレシートを割り当てる。
 *
 * visit_id が入っていればそれが正。入っていない場合でも、場所が同じで
 * 買った時刻が滞在の中に収まっていれば、その滞在での買い物とみなす。
 */
export const buildVisitReceiptMap = (
  visits: OverlandVisit[],
  receipts: ReceiptSummary[],
): Map<number, ReceiptSummary[]> => {
  const map = new Map<number, ReceiptSummary[]>()
  if (visits.length === 0 || receipts.length === 0) return map

  const push = (visitId: number, receipt: ReceiptSummary) => {
    const current = map.get(visitId)
    if (current) current.push(receipt)
    else map.set(visitId, [receipt])
  }

  const visitIds = new Set(visits.map((visit) => visit.id))

  for (const receipt of receipts) {
    if (receipt.visit_id !== null && visitIds.has(receipt.visit_id)) {
      push(receipt.visit_id, receipt)
      continue
    }
    if (receipt.place_id === null || receipt.purchased_at === null) continue

    const purchasedAt = parseUtcDate(receipt.purchased_at).getTime()
    const matched = visits.find((visit) => visit.place_id === receipt.place_id && isWithinVisit(visit, purchasedAt))
    if (matched) push(matched.id, receipt)
  }

  return map
}

/** どの滞在にも入らなかった買い物。タイムラインに単独の行として出す */
export type UnassignedSpending =
  | { kind: 'receipt'; receipt: ReceiptSummary; timestamp: Date }
  | { kind: 'card'; transaction: CardTransaction; timestamp: Date }

/**
 * 滞在に割り当てられなかったレシート・カード利用を時刻付きで返す。
 *
 * 合計金額には入っているのに画面のどこにも出ない買い物をなくすため。
 * 時刻が分からないもの（日付だけの売上到着分など）は dayStart に置く。
 */
export const listUnassignedSpendings = (
  receipts: ReceiptSummary[],
  visitReceiptMap: Map<number, ReceiptSummary[]>,
  transactions: CardTransaction[],
  visitCardTxMap: Map<number, CardTransaction[]>,
  dayStart: Date,
): UnassignedSpending[] => {
  const assignedReceiptIds = new Set([...visitReceiptMap.values()].flat().map((receipt) => receipt.id))
  const assignedTxIds = new Set([...visitCardTxMap.values()].flat().map((tx) => tx.id))

  const receiptRows: UnassignedSpending[] = receipts
    .filter((receipt) => !assignedReceiptIds.has(receipt.id))
    .map((receipt) => ({
      kind: 'receipt',
      receipt,
      timestamp: receipt.purchased_at ? parseUtcDate(receipt.purchased_at) : dayStart,
    }))
  const cardRows: UnassignedSpending[] = transactions
    .filter((tx) => !assignedTxIds.has(tx.id))
    .map((tx) => ({
      kind: 'card',
      transaction: tx,
      timestamp: tx.used_at ? parseUtcDate(tx.used_at) : dayStart,
    }))

  return [...receiptRows, ...cardRows]
}

/**
 * 滞在ごとのカード利用を割り当てる。
 *
 * カード会社の加盟店名は場所と結び付かないので、利用時刻が滞在の中に収まるかだけで決める。
 * 時刻の無い売上到着分はどの滞在にも入れない。
 */
export const buildVisitCardTransactionMap = (
  visits: OverlandVisit[],
  transactions: CardTransaction[],
): Map<number, CardTransaction[]> => {
  const map = new Map<number, CardTransaction[]>()

  for (const tx of transactions) {
    if (tx.used_at === null) continue
    const usedAt = parseUtcDate(tx.used_at).getTime()
    const matched = visits.find((visit) => isWithinVisit(visit, usedAt))
    if (!matched) continue
    map.set(matched.id, [...(map.get(matched.id) ?? []), tx])
  }

  return map
}
